import { Badge, Box, Heading, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTransactions } from "../context/TransactionsContext";
import { formatEuro } from "../utils/helperFunctions";

type GroupedData = {
  [month: string]: {
    income: number;
    expense: number;
  };
};

//For Testing
//@typescript-eslint/no-unused-vars
const generateDummyTransactions = () => {
  const transactions = [];
  const months = [
    "Jan 2023",
    "Feb 2023",
    "Mar 2023",
    "Apr 2023",
    "May 2023",
    "Jun 2023",
    "Jul 2023",
    "Aug 2023",
    "Sep 2023",
    "Oct 2023",
    "Nov 2023",
    "Dec 2023",
  ];

  for (let i = 0; i < 50; i++) {
    const randomMonth = months[Math.floor(Math.random() * months.length)];
    const type = Math.random() > 0.5 ? "income" : "expense";
    const amount = Math.floor(Math.random() * 500) + 50;
    const date = new Date(randomMonth + " 01, 2023").toISOString();

    transactions.push({
      type,
      amount,
      date,
      description: `Transaction ${i}`,
      category: type === "income" ? "Salary" : "Shopping",
    });
  }

  return transactions;
};

const groupTransactionsByMonth = (transactions: any[]) => {
  const groupedData: GroupedData = transactions.reduce(
    (acc, transaction) => {
      const transactionDate = new Date(transaction.date);
      if (isNaN(transactionDate.getTime())) return acc;

      const month = transactionDate.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      if (!acc[month]) {
        acc[month] = { income: 0, expense: 0 };
      }

      if (transaction.type === "income") {
        acc[month].income += transaction.amount;
      } else {
        acc[month].expense += transaction.amount;
      }

      return acc;
    },
    {} as Record<string, { income: number; expense: number }>,
  );

  return Object.entries(groupedData).map(([month, data]) => ({
    month,
    income: data.income,
    expense: data.expense,
  }));
};

export const MonthlyChart = () => {
  //For Testing
  /* const transactions = generateDummyTransactions(); */
  const { transactions } = useTransactions();
  const monthlyData = groupTransactionsByMonth(transactions);

  // Höchste Einnahmen und Ausgaben finden
  const highestIncome = monthlyData.reduce(
    (acc, curr) =>
      curr.income > acc.amount
        ? { amount: curr.income, month: curr.month }
        : acc,
    { amount: 0, month: "" },
  );

  const highestExpense = monthlyData.reduce(
    (acc, curr) =>
      curr.expense > acc.amount
        ? { amount: curr.expense, month: curr.month }
        : acc,
    { amount: 0, month: "" },
  );

  return (
    <Box
      mt={6}
      mb={40}
      textAlign="center"
      width={{
        base: window.innerWidth,
        md: "768px",
        lg: "992px",
        xl: "1200px",
      }}
      height="350px"
    >
      <Heading size="lg" mb={8} mt={0}>
        Einnahmen und Ausgaben | pro Monat
      </Heading>
      <Box mb={4}>
        <Badge mr={2} fontSize="sm" bg="#e1e1e1">
          Höchste Einnahme:{" "}
          <Text color="green">{formatEuro(highestIncome.amount)}</Text> -{" "}
          {highestIncome.month}
        </Badge>
        <Badge fontSize="sm" mt={{ base: 4 }} mb={{ base: 4 }} bg="#e1e1e1">
          Höchste Ausgabe:{" "}
          <Text color="red">{formatEuro(highestExpense.amount)}</Text> -{" "}
          {highestExpense.month}
        </Badge>
      </Box>
      <ResponsiveContainer>
        <BarChart
          data={monthlyData}
          margin={{ top: 20, right: 20, left: 40, bottom: 20 }}
          barSize={40}
        >
          {/*  <CartesianGrid strokeDasharray="3 3" /> */}
          <XAxis dataKey="month" />
          <YAxis tickFormatter={formatEuro} />
          <Tooltip
            formatter={(value: number) => formatEuro(value)}
            labelStyle={{ fontWeight: "bold" }}
          />
          <Legend />
          <Bar dataKey="income" fill="#38A169" name="Einnahmen" stackId="a" />
          <Bar dataKey="expense" fill="#E53E3E" name="Ausgaben" stackId="a" />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
